import { useState } from 'react';
import { payrollPaidPctOf } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { formatEur, formatEtDate, monthLabel } from '@/lib/cashFlow';
import {
  PAYROLL_LINE_STATUS_LABELS_ET,
  formatCentsForInput,
  parseEurInput,
  type LoanRequestOptions,
  type PayrollEmployeeLine,
} from '@/lib/loans';
import {
  usePayrollEmployees,
  useRecordPayrollEmployeePayment,
  useDeletePayrollEmployeePayment,
  useSettlePayrollRemaining,
} from '@/hooks/useLoans';
import { EtDateInput } from '@/components/ui/EtDateInput';

/**
 * Töötasude tasumine töötajate kaupa (approved canvas: loans-payroll /
 * PayrollSettle). ONE shared surface for a month's `payroll:<M>:net` entry —
 * embedded by the enriched-entry detail (both lenses' drill, L14) AND opened
 * from the payroll panel. Every number (outstanding / status / cap / totals)
 * arrives SERVER-derived via finance-core; this pane renders and never
 * computes money (L9 + master rule). Amounts entered here become
 * cash_flow_payment events mirrored onto the month entry (0037 RPC), so the
 * calendar chip / register jääk update through the shared query layer.
 */
export interface PayrollSettlePaneProps {
  /** `YYYY-MM` of the payroll month (from `payroll:<M>:net`). */
  month: string;
  /** Injected ISO day — deterministic tests, Tallinn browser clock in prod. */
  today: string;
  options?: LoanRequestOptions;
}

function StatusBadge({ line }: { line: PayrollEmployeeLine }) {
  if (line.status === 'settled') {
    const last = line.payments[line.payments.length - 1];
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--income))]/30 bg-[hsl(var(--income))]/10 px-2 py-0.5 text-[11px] text-[hsl(var(--income))]">
        {PAYROLL_LINE_STATUS_LABELS_ET.settled}
        {last ? ` · ${formatEtDate(last.paidOn)}` : ''}
      </span>
    );
  }
  if (line.status === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-500">
        {PAYROLL_LINE_STATUS_LABELS_ET.partial} · jääk {formatEur(line.outstandingCents)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">
      {PAYROLL_LINE_STATUS_LABELS_ET.unpaid}
    </span>
  );
}

interface PaymentForm {
  lineId: string;
  amountText: string;
  paidOn: string;
}

export function PayrollSettlePane({ month, today, options }: PayrollSettlePaneProps) {
  const { data, isLoading, isError } = usePayrollEmployees(month, options);
  const record = useRecordPayrollEmployeePayment(month, options);
  const removePayment = useDeletePayrollEmployeePayment(month, options);
  const settleAll = useSettlePayrollRemaining(month, options);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState<PaymentForm | null>(null);
  const [confirmingSettleAll, setConfirmingSettleAll] = useState(false);

  if (isLoading) {
    return <p className="p-3 text-xs text-muted-foreground">Laen töötajaid…</p>;
  }
  if (isError || !data) {
    return <p className="p-3 text-xs text-red-400">Töötajate laadimine ebaõnnestus.</p>;
  }

  const [year, monthNo] = month.split('-').map(Number);
  const label = monthLabel(year, monthNo);

  if (data.lines.length === 0) {
    return (
      <p className="p-3 text-xs text-muted-foreground">
        {label} — töötajate read puuduvad. Impordi palgaleht (PDF) palgafondi paneelist, et märkida
        tasumist töötajate kaupa.
      </p>
    );
  }

  const { totals } = data;
  const pct = payrollPaidPctOf(totals);
  const openCount = totals.lineCount - totals.settledLines;

  const toggleRow = (line: PayrollEmployeeLine) => {
    record.reset();
    removePayment.reset();
    if (expandedId === line.id) {
      setExpandedId(null);
      setForm(null);
      return;
    }
    setExpandedId(line.id);
    setForm({ lineId: line.id, amountText: formatCentsForInput(line.capCents), paidOn: today });
  };

  // "Lisa makse" renders ONLY inside the row whose form is open and enables ONLY
  // once the amount parses positive — both are invariants here, so `!` keeps the
  // branch graph clean (guard arms would be unreachable and break 100% coverage).
  const submitPayment = (line: PayrollEmployeeLine) => {
    record.mutate(
      {
        lineId: line.id,
        draft: {
          amountCents: parseEurInput(form!.amountText)!,
          paidOn: form!.paidOn,
          idempotencyKey: crypto.randomUUID(),
        },
      },
      { onSuccess: () => setForm({ lineId: line.id, amountText: '', paidOn: form!.paidOn }) },
    );
  };

  const quickSettle = (line: PayrollEmployeeLine) => {
    record.mutate({
      lineId: line.id,
      draft: { amountCents: line.capCents, paidOn: today, idempotencyKey: crypto.randomUUID() },
    });
  };

  const errorMessage = record.error?.message ?? removePayment.error?.message ?? settleAll.error?.message ?? null;
  const busy = record.isPending || removePayment.isPending || settleAll.isPending;
  const formAmountCents = form ? parseEurInput(form.amountText) : null;

  return (
    <div className="flex flex-col gap-3" data-testid="payroll-settle-pane">
      {/* koondprogress — totals are finance-core sums, pct payrollPaidPctOf */}
      <div>
        <div className="mb-1.5 flex items-center justify-between text-[12px]">
          <span className="text-muted-foreground">
            Tasutud <b className="text-foreground tabular-nums">{formatEur(totals.paidCents)}</b> /{' '}
            <span className="tabular-nums">{formatEur(totals.netCents)}</span>
          </span>
          <span
            className={cn(
              'font-semibold tabular-nums',
              totals.outstandingCents > 0 ? 'text-yellow-500' : 'text-[hsl(var(--income))]',
            )}
          >
            {totals.outstandingCents > 0 ? `Jääk ${formatEur(totals.outstandingCents)}` : 'Kõik tasutud'}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[hsl(var(--income))]"
            style={{ width: `${Math.min(100, pct)}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Tasutud osakaal"
          />
        </div>
      </div>

      {errorMessage && (
        <p role="alert" className="text-xs text-red-400">
          {errorMessage}
        </p>
      )}

      {/* töötajate read */}
      <div className="overflow-hidden rounded-lg border border-border">
        {data.lines.map((line) => {
          const expanded = expandedId === line.id;
          return (
            <div key={line.id} className="border-b border-border/50 last:border-0">
              <div className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-accent/30">
                <button
                  type="button"
                  onClick={() => toggleRow(line)}
                  aria-expanded={expanded}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  <span aria-hidden className="text-[10px] text-muted-foreground">
                    {expanded ? '▾' : '▸'}
                  </span>
                  <span className="truncate text-[12px] font-medium">{line.name}</span>
                  <StatusBadge line={line} />
                </button>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-[12px] tabular-nums text-muted-foreground">
                    {line.status === 'partial' ? `${formatEur(line.paidCents)} / ` : ''}
                    {formatEur(line.netCents)}
                  </span>
                  {line.status !== 'settled' && line.capCents > 0 && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => quickSettle(line)}
                      className="rounded-md border border-[hsl(var(--income))]/40 bg-[hsl(var(--income))]/10 px-2 py-1 text-[11px] font-medium text-[hsl(var(--income))] hover:bg-[hsl(var(--income))]/20 disabled:opacity-50"
                    >
                      Märgi tasutuks
                    </button>
                  )}
                </div>
              </div>

              {expanded && (
                <div className="border-t border-border/40 bg-background/40 px-6 py-2.5">
                  {line.payments.length > 0 && (
                    <>
                      <div className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                        Maksed
                      </div>
                      <ul className="mb-2.5 flex flex-col gap-1">
                        {line.payments.map((payment) => (
                          <li key={payment.id} className="flex items-center justify-between text-[12px]">
                            <span className="text-muted-foreground">{formatEtDate(payment.paidOn)}</span>
                            <span className="flex items-center gap-2">
                              <span className="tabular-nums">{formatEur(payment.amountCents)}</span>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => removePayment.mutate(payment.id)}
                                className="rounded px-1.5 py-0.5 text-[11px] text-red-400/80 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                              >
                                Eemalda
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {/* Inside the expanded row `form` is ALWAYS this row's form (they are
                      set together in toggleRow), so `!` — a null-check arm here would
                      be unreachable and break the 100% branch gate. */}
                  {line.capCents > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5">
                        <span className="text-[11px] text-muted-foreground">Summa</span>
                        <input
                          aria-label={`${line.name} makse summa`}
                          value={form!.amountText}
                          onChange={(e) => setForm({ ...form!, amountText: e.target.value })}
                          className="w-20 bg-transparent text-right text-[12px] tabular-nums outline-none"
                        />
                        <span className="text-[11px] text-muted-foreground">€</span>
                      </label>
                      <EtDateInput
                        aria-label={`${line.name} makse kuupäev`}
                        value={form!.paidOn}
                        onChangeIso={(iso) => setForm({ ...form!, paidOn: iso })}
                        className="rounded-md border border-border bg-card px-2 py-1.5 text-[12px]"
                      />
                      <button
                        type="button"
                        disabled={busy || formAmountCents === null || formAmountCents <= 0}
                        onClick={() => submitPayment(line)}
                        className="rounded-md bg-[hsl(var(--primary))] px-2.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--primary-foreground))] hover:opacity-90 disabled:opacity-50"
                      >
                        Lisa makse
                      </button>
                      <span className="text-[11px] text-muted-foreground">
                        jääk eeltäidetud — osaliseks muuda summat
                      </span>
                    </div>
                  ) : (
                    line.payments.length === 0 && (
                      <p className="text-[11px] text-muted-foreground">Makseid pole.</p>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* jalus: kõigi jääkide korraga tasumine (server arvutab summad uuesti) */}
      <div className="flex items-center justify-between gap-2">
        {totals.outstandingCents > 0 && data.entry ? (
          confirmingSettleAll ? (
            <span className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  settleAll.mutate(
                    { idempotencyKey: crypto.randomUUID(), paidOn: today },
                    { onSuccess: () => setConfirmingSettleAll(false) },
                  )
                }
                className="rounded-md bg-[hsl(var(--primary))] px-3 py-1.5 text-[12px] font-semibold text-[hsl(var(--primary-foreground))] hover:opacity-90 disabled:opacity-50"
              >
                Kinnita: {openCount} töötaja jääk ({formatEur(totals.outstandingCents)})
              </button>
              <button
                type="button"
                onClick={() => setConfirmingSettleAll(false)}
                className="rounded-md px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                Loobu
              </button>
            </span>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                settleAll.reset();
                setConfirmingSettleAll(true);
              }}
              className="rounded-md border border-border px-3 py-1.5 text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              Märgi kõik ülejäänu tasutuks…
            </button>
          )
        ) : (
          <span />
        )}
        <span className="text-[11px] text-muted-foreground">
          Iga makse jääb auditijälge; kirje muutub tasutuks, kui kõik töötajad on kaetud.
        </span>
      </div>
    </div>
  );
}
